import { useCallback, useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSelector } from "react-redux";
import { resolveSponsorAccess } from "../utils/sponsorAccessContext";
import { isVqaOnlyOrg } from "../utils/vqaOnlyOrg";
import {
  canOpenMyVendorRequest,
  getMyVendorRequestAccessFlags,
  normalizeMyVendorSubRoles,
} from "../utils/myVendorsRequestAccess";

/**
 * Resolves sponsor sub-roles for My Vendors request click lockdown.
 * Prefers Layout-hydrated profile (same source as Sidebar), then Clerk metadata.
 */
export default function useMyVendorRequestAccess() {
  const { user } = useUser();
  const loggedInUser = useSelector((state) => state.loggedInUser);
  const reduxRights = useSelector((state) => state.userRights?.rights) || [];

  const subRoles = useMemo(() => {
    const profile = loggedInUser?.userDetails;
    const fromProfile = normalizeMyVendorSubRoles(profile?.sub_role);
    if (fromProfile.length > 0) return fromProfile;

    const fromDto = normalizeMyVendorSubRoles(profile?.sponsor_subrole_dto);
    if (fromDto.length > 0) return fromDto;

    const { subRoles: resolved } = resolveSponsorAccess({
      clerkMetadata: user?.publicMetadata || {},
      sponsorProfile: profile,
      reduxRights,
    });
    return normalizeMyVendorSubRoles(resolved);
  }, [loggedInUser?.userDetails, reduxRights, user?.publicMetadata]);

  const isVqaOnly = isVqaOnlyOrg(loggedInUser?.userDetails);
  const { canOpenVqq, canOpenVqa: canRequestVqa } = useMemo(
    () => getMyVendorRequestAccessFlags(subRoles),
    [subRoles]
  );
  const canRequestVqq = !isVqaOnly && canOpenVqq;

  const canOpenRequest = useCallback(
    (request) => canOpenMyVendorRequest(request, subRoles, { isVqaOnly }),
    [subRoles, isVqaOnly]
  );

  return {
    canOpenRequest,
    canRequestVqq,
    canRequestVqa,
    isVqaOnly,
    subRoles,
  };
}
